<script lang="ts">
import { defineComponent as DO_defineComponent } from 'vue';
export default /*#__PURE__*/ DO_defineComponent({
	name: 'ToInput'
});
</script>
<template>
	<label :id="id" :key="langKey" v-tip="changed ? t('to.common.originalValue', valueStore[0]) : ''" :flex="flex ? flex : null" class="to-input" :class="setClass" :style="setStyle" @click="click">
		<span v-if="verifiable && (rule || required || ToFormItem.required)" v-show="false" :trim="trim" :req="validateRequired" class="to-validate" :desc="validateDesc" :rule="rule" :value="data"></span>

		<span class="to-input-wrapper">
			<to-icon v-if="props.step" v-dis="Number(data) === Number(props.min) || props.disabled" v-press="reduce" class="to-input-decrease" :value="props.stepIconMode ? 'expand' : 'reduce'" link />

			<!-- 前缀 -->
			<span v-if="$slots.prefix || props.step" class="to-input-prefix">
				<slot name="prefix" />
			</span>

			<span v-if="props.prefix" class="to-input-prefix-text">
				<span>{{ props.prefix }}</span>
			</span>

			<!-- 输入框部分 -->
			<span v-if="props.type === 'textarea'" class="to-input-content">
				<textarea ref="inputRef" v-model="data" class="to-input-box" :autocomplete="props.autocomplete ? 'on' : 'new-password'" :maxlength="props.maxlength" :placeholder="iPlaceholder" :disabled="props.disabled" :tabindex="props.tabindex" @keyup="inputKeyup" @keydown.exact="inputKeydown" @cut="inputCut" @focus="inputFocus" @blur="inputBlur" @paste="inputPaste" @keypress="inputKeyPress" @change="inputChange" @input="inputInput" @keydown.enter.exact="inputEnter" @keydown.ctrl.enter.exact="inputCtrlEnter" @keydown.meta.enter.exact="inputCtrlEnter" />
			</span>

			<span v-else-if="props.type === 'html'" class="to-input-html">
				{{ props.html }}
				<slot name="html" />
			</span>

			<span v-else class="to-input-content">
				<to-icon v-if="icon" :value="icon" class="to-input-icon"></to-icon>
				<input ref="inputRef" v-model="data" class="to-input-box" :autocomplete="props.autocomplete ? 'on' : 'new-password'" :tabindex="props.tabindex" :maxlength="props.maxlength" :placeholder="iPlaceholder" :type="itype" :disabled="props.disabled" @keyup="inputKeyup" @keydown.exact="inputKeydown" @cut="inputCut" @focus="inputFocus" @blur="inputBlur" @paste="inputPaste" @keypress="inputKeyPress" @change="inputChange" @input="inputInput" @keydown.enter.exact="inputEnter" @keydown.ctrl.enter.exact="inputCtrlEnter" @keydown.meta.enter.exact="inputCtrlEnter" />
			</span>

			<to-icon v-if="!props.disabled && props.clearable && !props.step" v-show="clearVisible" class="to-input-clear" link value="close" @click.stop="clear" />

			<span v-if="props.maxlength !== Infinity && props.count && props.type !== 'textarea'" class="to-input-count">
				<span class="to-input-count-text">{{ getDataLength }}/{{ props.maxlength }}</span>
			</span>

			<!-- 后缀区 -->
			<span v-if="props.suffix" class="to-input-suffix-text">
				<span>{{ props.suffix }}</span>
			</span>

			<span v-if="($slots.default || $slots.suffix || props.step) && type !== 'textarea'" class="to-input-suffix">
				<slot />
				<slot name="suffix" />
			</span>

			<to-icon v-if="props.step" v-dis="Number(data) === Number(props.max) || props.disabled" v-press="increase" class="to-input-increase" :value="props.stepIconMode ? 'collapse' : 'add'" link />
		</span>

		<span v-if="props.type === 'textarea' && ((props.maxlength !== Infinity && props.count) || $slots.fn || $slots.default || props.suggestion)" class="to-input-fn">
			<span v-if="$slots.fn || $slots.default" class="to-input-suffix-slot">
				<slot v-if="type === 'textarea'" />
				<slot v-if="type === 'textarea'" name="fn" />
			</span>
			<span class="to-input-suggestion">
				<template v-if="props.suggestion">
					<ToButton v-for="(item, i) in props.suggestion.split(',')" :key="i" class="to-input-suggestion-item" @click="addSuggestion(item)">{{ item }}</ToButton>
				</template>
			</span>
			<span v-if="props.maxlength !== Infinity && props.count" class="to-input-count">
				<span class="to-input-count-text">{{ getDataLength }}/{{ props.maxlength }}</span>
			</span>
		</span>

		<span v-if="errorTip" v-color="'danger'" class="to-box-tip">
			<template v-if="errorType === 'rule'">
				{{ t('to.input.inputError') }}
				<to-icon v-tip.danger="errorTip" style="height: auto" value="help" color="danger" mode="toast"></to-icon>
			</template>
			<template v-else> {{ t('to.input.required') }} </template>
		</span>
	</label>
</template>
<script lang="ts" setup>
import { ref, computed, watch, CSSProperties, onMounted, inject, nextTick, getCurrentInstance } from 'vue'
import { ToIcon, ToButton } from '@touchvue/components'
import { vDis, vTip, vPress } from '@touchvue/directives'
import { t, langKey } from '@touchvue/locale'

const ins = getCurrentInstance()


const props = defineProps({
	flex: Boolean,
	// 是否自动获取焦点
	autofocus: {
		type: Boolean,
		default: false
	},
	desc: {
		type: String,
		default: ''
	},
	rule: {
		type: [String, Boolean],
		default: ''
	},
	verifiable: {
		type: Boolean,
		default: true
	},
	required: {
		type: Boolean,
		default: null
	},
	validateTip: {
		type: String,
		default: ''
	},
	propErrorType: {
		type: String,
		default: ''
	},
	propErrorTip: {
		type: String,
		default: ''
	},
	id: {
		type: String,
		default: ''
	},
	placeholder: {
		type: String,
		default: ''
	},
	type: {
		type: String,
		default: 'text'
	},
	icon: {
		type: String,
		default: ''
	},
	tabindex: {
		type: String,
		default: '1'
	},
	autocomplete: {
		type: Boolean,
		default: false
	},
	disabled: {
		type: Boolean,
		default: false
	},
	max: {
		type: [String, Number],
		default: Infinity
	},
	min: {
		type: [String, Number],
		default: -Infinity
	},
	maxlength: {
		type: [String, Number],
		default: Infinity
	},
	dot: {
		type: [String, Number],
		default: 0
	},
	step: {
		type: [String, Number],
		default: null
	},
	stepIconMode: {
		type: String,
		default: ''
	},
	spChars: {
		type: [String, Boolean],
		default: ''
	},
	prefix: {
		type: String,
		default: ''
	},
	suffix: {
		type: String,
		default: ''
	},
	suggestion: {
		type: String,
		default: ''
	},
	suggestionType: {
		type: String,
		default: ''
	},
	suggestionSymbol: {
		type: String,
		default: '、'
	},
	clearable: {
		type: Boolean,
		default: true
	},
	preventFocus: {
		type: Boolean,
		default: false
	},
	comparable: {
		type: Boolean,
		default: false
	},
	modelValue: {
		type: [String, Number],
		default: () => ''
	},
	html: {
		type: [String, Number],
		default: ''
	},
	trim: {
		type: Boolean,
		default: true
	},
	color: {
		type: String,
		default: 'default'
	},
	placeholderColor: {
		type: String,
		default: ''
	},
	mode: {
		type: String,
		default: 'default'
	},
	width: {
		type: [Number, String],
		default: ''
	},
	height: {
		type: [Number, String],
		default: ''
	},
	link: {
		type: Boolean,
		default: false
	},
	conversionType: {
		type: [String, Number],
		default: 0
	},
	count: {
		type: Boolean,
		default: false
	}
})

const emit = defineEmits(['update:modelValue', 'click', 'clear', 'input', 'keyup', 'change', 'keydown', 'cut', 'paste', 'keypress', 'focus', 'blur', 'enter', 'ctrlenter'])

const ToForm = inject<{ comparable?: boolean }>('ToForm', {})
const ToFormItem = inject<{ required?: boolean; label?: string }>('ToFormItem', {})

const errorTip = ref('')
const errorType = ref('rule')
const valueStore = ref([])
// const prevent = ref(false)
// const inited = ref(false)
const focused = ref(false)
const data = ref(props.modelValue)
const clearVisible = ref(false)
const itype = ref('text')
// const disabled = computed(() => props.disabled)
const disabled = ref(props.disabled)
// const multipleNumber = ref(1)
const inputRef = ref<HTMLInputElement | HTMLTextAreaElement | null>(null)

watch(
	() => props.modelValue,
	val => {
		clearVisible.value = !!val
		data.value = val
	},
	{ immediate: true, deep: true }
)
watch(
	() => props.type,
	val => {
		itype.value = val
	}
)

watch(
	() => data.value,
	val => {
		errorTip.value = ''
		clearVisible.value = !!val
		if (props.type === 'number') {
			// 先转换为纯数字字符串
			data.value = (data.value + '').replace(/[^0-9.-]/g, '')
			// 多重负号去重
			if (data.value.indexOf('-') > -1) {
				if (data.value.indexOf('-') === 0) {
					if (data.value.indexOf('.') === 1) {
						data.value = '-0' + data.value.replace(/[-]/g, '')
					} else {
						data.value = '-' + data.value.replace(/[-]/g, '')
					}
				} else {
					data.value = data.value.replace(/[-]/g, '')
				}
			}
			//小数点去重
			data.value = data.value.replace(/(\..*?)\./g, '$1')
			// 小数点长度判断
			if (data.value.indexOf('.') > -1) {
				// 小圆点开头的自动补 0
				if (data.value.indexOf('.') === 0) {
					data.value = '0' + data.value
				}

				// 分别获取和处理小数点前后的字符
				let pre = data.value.substring(0, data.value.indexOf('.'))
				let suf = data.value.substring(data.value.indexOf('.') + 1)
				suf = suf.replace(/[/.]/g, '').substring(0, Number(props.dot))

				// 拼接字符串
				data.value = `${pre}.${suf}`
			}

			if (Number(data.value) < Number(props.min)) {
				data.value = props.min
			}
			if (Number(data.value) > Number(props.max)) {
				data.value = props.max
			}

			if (String(data.value).length > Number(props.maxlength)) {
				data.value = Number(String(data.value).slice(0, Number(props.maxlength)))
			}
		}
		if (iSpChars.value) {
			const filterData = filter(iSpChars.value, val)
			if (filterData !== false) {
				data.value = filterData
			}
		}
		emit('update:modelValue', data.value)
	}
)

watch(
	() => props.propErrorType,
	val => {
		errorType.value = val
	}
)

watch(
	() => props.propErrorTip,
	val => {
		errorTip.value = val
	}
)

watch(
	() => props.autofocus,
	val => {
		if (val) {
			focusInput()
		}
	}
)

const filter = (chars, from) => {
	if (chars && from) {
		const check = new RegExp(
			`[${chars
				.split(',')
				.map(char => '\\' + char)
				.join('')}]+`,
			''
		)
		const reg = new RegExp(
			`[${chars
				.split(',')
				.map(char => '\\' + char)
				.join('')}]*`,
			'g'
		)
		if (check.test(from)) {
			return from.replace(reg, '')
		} else {
			return false
		}
	} else {
		return false
	}
}

const focusInput = () => {
	if (inputRef.value) {
		try {
			// 确保元素未被禁用
			if (!inputRef.value.disabled) {
				// 延迟聚焦，给元素更多准备时间
				setTimeout(() => {
					inputRef.value?.focus()
				}, 100)
			}
		} catch (error) {
			console.error('输入框聚焦失败:', error)
		}
	}
}

onMounted(() => {
	data.value = props.modelValue
	itype.value = props.type
	if (props.autofocus) {
		nextTick(() => {
			focusInput()
		})
	}
})

const iPlaceholder = computed(() => {
	if (!disabled.value && !props.placeholder) {
		return t('to.common.placeholder.input')
	} else {
		return props.placeholder
	}
})

const iSpChars = computed(() => {
	if (props.spChars) {
		return props.spChars
	}
	return ins.appContext.config.globalProperties.$TouchUI?.input?.spChars ?? '/,\\,[,],{,},"'
})

const setClass = computed(() => {
	const arr = ['to-disable-target']
	if (errorTip.value) {
		arr.push('to-input-mode-line')
		arr.push('to-input-color-danger')
		arr.push('to-input-tip-show')
	} else {
		if (props.mode) {
			for (let i = 0; i < props.mode.split(' ').length; i++) {
				arr.push(`mode-${props.mode.split(' ')[i]}`)
			}
		}
		if (props.type) {
			arr.push(`type-${props.type}`)
		}
		if (props.disabled || disabled.value) {
			arr.push(`is-disabled`)
		}
		if (focused.value) {
			arr.push('is-focused')
		}
		if (props.color) {
			arr.push(`color-${props.color}`)
		}
		if (props.link) {
			arr.push('is-link')
		}
		if (props.step) {
			arr.push('is-step')
		}
		if ((ToForm && ToForm.comparable) || props.comparable) {
			arr.push('to-compare')
		}
		if (changed.value) {
			arr.push('to-input-changed')
		}
	}
	return arr
})

const setStyle = computed(() => {
	const obj: CSSProperties = {}
	if (props.width) {
		if (!isNaN(Number(props.width))) {
			obj.width = props.width + 'em'
		} else {
			obj.width = props.width
		}
	}
	if (props.height) {
		if (!isNaN(Number(props.height))) {
			obj.height = props.height + 'em'
		} else {
			obj.height = props.height
		}
	}
	return obj
})

const changed = computed(() => {
	if ((props.comparable || ToForm?.comparable) && valueStore.value.length > 0 && data.value !== valueStore.value[0]) {
		return true
	} else {
		return false
	}
})

const validateRequired = computed(() => {
	if (props.required === false) {
		return 'false'
	} else if (props.required) {
		return 'true'
	} else if (ToFormItem && ToFormItem.required) {
		return 'true'
	} else {
		return 'false'
	}
})

const validateDesc = computed(() => {
	if (props.desc) {
		return props.desc
	} else if (ToFormItem && ToFormItem.label) {
		return ToFormItem.label
	} else {
		return ''
	}
})

const getDataLength = computed(() => {
	if (data.value || data.value === 0) {
		return String(data.value).length
	} else {
		return 0
	}
})

const setDisabled = (f: boolean) => {
	disabled.value = f
}

function inputKeyup(e) {
	emit('keyup', data.value, e)
}
function inputChange() {
	emit('change', data.value)
}
function inputKeydown(e) {
	emit('keydown', data.value, e)
}
function inputCut() {
	emit('cut', data.value)
}
function inputFocus() {
	errorTip.value = ''
	if (props.disabled) return false
	focused.value = true
	emit('focus', data.value)
}
function inputBlur() {
	if (props.disabled) return false
	focused.value = false
	if (props.trim && data.value && typeof data.value === 'string') {
		data.value = String(data.value).trim()
	}
	emit('blur', data.value)
}
function inputPaste() {
	emit('paste', data.value)
}
function inputKeyPress(e) {
	emit('keypress', data.value, e)
}
function inputInput() {
	emit('input', data.value)
}
function inputEnter() {
	emit('enter', data.value)
}
function inputCtrlEnter() {
	emit('ctrlenter', data.value)
}
function clear() {
	data.value = ''
	clearVisible.value = false
	emit('clear')
}
function reduce() {
	data.value = Number(data.value) - Number(props.step)
}
function increase() {
	data.value = Number(data.value) + Number(props.step)
}
function addSuggestion(item: any) {
	data.value += item
}

function click(e: MouseEvent) {
	// 须透传原生事件，父组件使用 @click.stop 等修饰符时才能调用 stopPropagation
	emit('click', e)
}
function compare() {
	valueStore.value.unshift(data.value)
}
// 撤销
function restore() {
	data.value = valueStore.value[0]
	valueStore.value.splice(0, 1)
}

defineExpose({
	compare,
	restore,
	changed,
	setDisabled,
	errorTip,
	errorType,
	focus: focusInput
})
</script>
